import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { UserRecord, UserFormData, SearchFilters, SortOption } from '../types';
import { getCurrentUser } from './auth';
import { createUserPermission } from './permissionService';

const USERS_COLLECTION = 'users';

// 새 사용자 생성
export const createUser = async (userData: UserFormData): Promise<string> => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    const now = Timestamp.now();
    
    const newUser: Omit<UserRecord, 'id'> = {
      ...userData,
      photo: {
        url: null,
        fileName: null,
        uploadedAt: null
      },
      appointmentPeriod: {
        start: Timestamp.fromDate(userData.appointmentPeriod.start),
        end: userData.appointmentPeriod.end ? Timestamp.fromDate(userData.appointmentPeriod.end) : null
      },
      initialEducationDate: userData.initialEducationDate ? Timestamp.fromDate(userData.initialEducationDate) : null,
      recentEducationDate: userData.recentEducationDate ? Timestamp.fromDate(userData.recentEducationDate) : null,
      initialEducationCert: {
        url: null,
        fileName: null,
        uploadedAt: null
      },
      recentEducationCert: {
        url: null,
        fileName: null,
        uploadedAt: null
      },
      createdAt: now,
      updatedAt: now,
      createdBy: currentUser.uid,
      updatedBy: currentUser.uid
    };

    const docRef = await addDoc(collection(db, USERS_COLLECTION), newUser);
    
    // 인원 생성 후 자동으로 열람자 권한 생성
    try {
      await createUserPermission(userData.email, userData.name, docRef.id, 'viewer');
    } catch (permissionError) {
      console.warn('권한 생성 중 오류 발생:', permissionError);
      // 권한 생성 실패해도 인원 생성은 성공으로 처리
    }
    
    return docRef.id;
  } catch (error: any) {
    console.error('사용자 생성 실패:', error);
    throw new Error(`사용자 생성 실패: ${error.message}`);
  }
};

// 사용자 업데이트
export const updateUser = async (userId: string, userData: Partial<UserFormData>): Promise<void> => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    const updateData: any = {
      ...userData,
      updatedAt: Timestamp.now(),
      updatedBy: currentUser.uid
    };

    // 발령기간이 있으면 Timestamp로 변환
    if (userData.appointmentPeriod) {
      updateData.appointmentPeriod = {
        start: Timestamp.fromDate(userData.appointmentPeriod.start),
        end: userData.appointmentPeriod.end ? Timestamp.fromDate(userData.appointmentPeriod.end) : null
      };
    }

    // 교육이수일이 있으면 Timestamp로 변환
    if (userData.initialEducationDate !== undefined) {
      updateData.initialEducationDate = userData.initialEducationDate ? Timestamp.fromDate(userData.initialEducationDate) : null;
    }
    if (userData.recentEducationDate !== undefined) {
      updateData.recentEducationDate = userData.recentEducationDate ? Timestamp.fromDate(userData.recentEducationDate) : null;
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, updateData);
  } catch (error: any) {
    console.error('사용자 업데이트 실패:', error);
    throw new Error(`사용자 업데이트 실패: ${error.message}`);
  }
};

// 사용자 삭제
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await deleteDoc(userRef);
  } catch (error: any) {
    console.error('사용자 삭제 실패:', error);
    throw new Error(`사용자 삭제 실패: ${error.message}`);
  }
};

// 사용자 한 명 조회
export const getUser = async (userId: string): Promise<UserRecord | null> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return {
        id: userDoc.id,
        ...userDoc.data()
      } as UserRecord;
    }
    
    return null;
  } catch (error: any) {
    console.error('사용자 조회 실패:', error);
    throw new Error(`사용자 조회 실패: ${error.message}`);
  }
};

// 모든 사용자 조회 (페이지네이션)
export const getUsers = async (
  filters?: SearchFilters,
  sortOption?: SortOption,
  pageSize: number = 50,
  lastDoc?: DocumentSnapshot
): Promise<{ users: UserRecord[]; lastDoc: DocumentSnapshot | null }> => {
  try {
    console.log('getUsers 호출됨:', { filters, sortOption, pageSize });
    let q = query(collection(db, USERS_COLLECTION));

    // 기본 필터만 서버에서 적용 (인덱스 에러 방지)
    let serverFiltersApplied = 0;
    if (filters) {
      console.log('필터 적용 중:', filters);
      
      // 인덱스가 필요하지 않은 단일 필터만 서버에서 처리
      if (filters.headquarters && serverFiltersApplied === 0) {
        console.log('본부 필터 적용:', filters.headquarters);
        q = query(q, where('headquarters', '==', filters.headquarters));
        serverFiltersApplied++;
      } else if (filters.department && serverFiltersApplied === 0) {
        console.log('부서 필터 적용:', filters.department);
        q = query(q, where('department', '==', filters.department));
        serverFiltersApplied++;
      } else if (filters.position && serverFiltersApplied === 0) {
        console.log('직위 필터 적용:', filters.position);
        q = query(q, where('position', '==', filters.position));
        serverFiltersApplied++;
      }
      // 복합 필터나 배열 필터는 클라이언트에서 처리
    }

    // 정렬 적용
    if (sortOption) {
      q = query(q, orderBy(sortOption.field, sortOption.direction));
    } else {
      q = query(q, orderBy('createdAt', 'desc'));
    }

    // 페이지네이션
    q = query(q, limit(pageSize));
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    
    const users: UserRecord[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as UserRecord));

    // 클라이언트 사이드 필터링 (모든 필터 조건 적용)
    let filteredUsers = users;
    
    if (filters) {
      console.log('클라이언트 사이드 필터링 시작:', { totalUsers: users.length, filters });
      
      filteredUsers = users.filter(user => {
        // 텍스트 검색
        if (filters.search) {
          const searchTerm = filters.search.toLowerCase();
          const matchesSearch = 
            user.name.toLowerCase().includes(searchTerm) ||
            user.employeeId.toLowerCase().includes(searchTerm) ||
            user.headquarters.toLowerCase().includes(searchTerm) ||
            user.department.toLowerCase().includes(searchTerm) ||
            user.position.toLowerCase().includes(searchTerm) ||
            user.role.toLowerCase().includes(searchTerm) ||
            (user.location && user.location.toLowerCase().includes(searchTerm)) ||
            (user.assignments && user.assignments.some(assignment => 
              assignment.toLowerCase().includes(searchTerm)
            ));
          if (!matchesSearch) return false;
        }
        
        // 본부 필터
        if (filters.headquarters && user.headquarters !== filters.headquarters) {
          return false;
        }
        
        // 부서 필터
        if (filters.department && user.department !== filters.department) {
          return false;
        }
        
        // 직위 필터
        if (filters.position && user.position !== filters.position) {
          return false;
        }
        
        // 지점/공항 필터
        if (filters.location && user.location !== filters.location) {
          return false;
        }
        
        // 발령사항 필터 (배열 필드)
        if (filters.assignments) {
          const hasAssignment = user.assignments && user.assignments.includes(filters.assignments);
          if (!hasAssignment) return false;
        }
        
        // 초기 보안교육일 필터
        if (filters.initialEducationStartDate || filters.initialEducationEndDate) {
          const userEducationDate = user.initialEducationDate;
          if (!userEducationDate) return false; // 교육일이 없으면 제외
          
          const educationDate = userEducationDate.toDate ? userEducationDate.toDate() : new Date(userEducationDate as any);
          
          if (filters.initialEducationStartDate) {
            const startDate = new Date(filters.initialEducationStartDate);
            if (educationDate < startDate) return false;
          }
          
          if (filters.initialEducationEndDate) {
            const endDate = new Date(filters.initialEducationEndDate);
            endDate.setHours(23, 59, 59, 999); // 해당 날짜 끝까지 포함
            if (educationDate > endDate) return false;
          }
        }
        
        // 정기 보안교육일 필터
        if (filters.recentEducationStartDate || filters.recentEducationEndDate) {
          const userEducationDate = user.recentEducationDate;
          if (!userEducationDate) return false; // 교육일이 없으면 제외
          
          const educationDate = userEducationDate.toDate ? userEducationDate.toDate() : new Date(userEducationDate as any);
          
          if (filters.recentEducationStartDate) {
            const startDate = new Date(filters.recentEducationStartDate);
            if (educationDate < startDate) return false;
          }
          
          if (filters.recentEducationEndDate) {
            const endDate = new Date(filters.recentEducationEndDate);
            endDate.setHours(23, 59, 59, 999); // 해당 날짜 끝까지 포함
            if (educationDate > endDate) return false;
          }
        }
        
        return true;
      });
      
      console.log('클라이언트 사이드 필터링 결과:', { filteredCount: filteredUsers.length });
    }

    const newLastDoc = querySnapshot.docs.length > 0 
      ? querySnapshot.docs[querySnapshot.docs.length - 1] 
      : null;

    return {
      users: filteredUsers,
      lastDoc: newLastDoc
    };
  } catch (error: any) {
    console.error('사용자 목록 조회 실패:', error);
    throw new Error(`사용자 목록 조회 실패: ${error.message}`);
  }
};

// 사번으로 사용자 검색
export const getUserByEmployeeId = async (employeeId: string): Promise<UserRecord | null> => {
  try {
    const q = query(
      collection(db, USERS_COLLECTION),
      where('employeeId', '==', employeeId),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as UserRecord;
  } catch (error: any) {
    console.error('사번으로 사용자 검색 실패:', error);
    throw new Error(`사용자 검색 실패: ${error.message}`);
  }
};

// 이메일로 사용자 검색 (이름 조회용)
export const getUserByEmail = async (email: string): Promise<UserRecord | null> => {
  try {
    const q = query(
      collection(db, USERS_COLLECTION),
      where('email', '==', email),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as UserRecord;
  } catch (error: any) {
    console.error('이메일로 사용자 검색 실패:', error);
    return null; // 실패해도 null 반환 (필수 정보가 아니므로)
  }
};

// 여러 이메일의 사용자 이름을 일괄 조회
export const getUserNamesByEmails = async (emails: string[]): Promise<Record<string, string>> => {
  try {
    if (emails.length === 0) return {};
    
    // 특별한 이메일들에 대한 기본 매핑
    const specialNames: Record<string, string> = {
      'safety7033@gmail.com': '시스템 관리자',
      'system': '시스템',
      'admin': '관리자'
    };
    
    const result: Record<string, string> = {};
    const needToQuery: string[] = [];
    
    // 특별한 이메일들 먼저 처리
    emails.forEach(email => {
      if (specialNames[email]) {
        result[email] = specialNames[email];
      } else {
        needToQuery.push(email);
      }
    });
    
    // 일반 사용자 이메일들 조회
    if (needToQuery.length > 0) {
      // Firestore 'in' 쿼리는 최대 10개까지만 지원하므로 나누어서 처리
      const chunks = [];
      for (let i = 0; i < needToQuery.length; i += 10) {
        chunks.push(needToQuery.slice(i, i + 10));
      }
      
      for (const chunk of chunks) {
        const q = query(
          collection(db, USERS_COLLECTION),
          where('email', 'in', chunk)
        );
        
        const querySnapshot = await getDocs(q);
        querySnapshot.docs.forEach(doc => {
          const userData = doc.data() as UserRecord;
          result[userData.email] = userData.name;
        });
      }
    }
    
    // 찾지 못한 이메일들은 이메일 자체를 이름으로 사용
    emails.forEach(email => {
      if (!result[email]) {
        result[email] = email;
      }
    });
    
    return result;
  } catch (error: any) {
    console.error('사용자 이름 일괄 조회 실패:', error);
    // 실패해도 이메일을 그대로 반환
    const fallback: Record<string, string> = {};
    emails.forEach(email => {
      fallback[email] = email;
    });
    return fallback;
  }
};

// 파일 정보 업데이트
export const updateUserFile = async (
  userId: string,
  fileType: 'photo' | 'initialEducationCert' | 'recentEducationCert',
  fileInfo: {
    url: string;
    fileName: string;
    uploadedAt: Timestamp;
  } | null
): Promise<void> => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const updateData: any = {
      [fileType]: fileInfo,
      updatedAt: Timestamp.now(),
      updatedBy: currentUser.uid
    };

    // 파일 업로드/삭제 로그 (날짜 필드는 더 이상 자동 업데이트하지 않음)
    console.log('updateUserFile 호출됨:', { userId, fileType, fileInfo });
    console.log('업데이트할 데이터:', updateData);

    await updateDoc(userRef, updateData);
  } catch (error: any) {
    console.error('파일 정보 업데이트 실패:', error);
    throw new Error(`파일 정보 업데이트 실패: ${error.message}`);
  }
};

// 배치 업데이트
export const batchUpdateUsers = async (updates: Array<{ id: string; data: Partial<UserRecord> }>): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const currentUser = getCurrentUser();
    
    if (!currentUser) {
      throw new Error('로그인이 필요합니다.');
    }

    updates.forEach(({ id, data }) => {
      const userRef = doc(db, USERS_COLLECTION, id);
      batch.update(userRef, {
        ...data,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser.uid
      });
    });

    await batch.commit();
  } catch (error: any) {
    console.error('배치 업데이트 실패:', error);
    throw new Error(`배치 업데이트 실패: ${error.message}`);
  }
};

// 통계 정보 가져오기 (확장 버전)
export const getUserStats = async (): Promise<{
  total: number;
  byHeadquarters: Record<string, number>;
  byDepartment: Record<string, number>;
  byPosition: Record<string, number>;
  byAssignments: Record<string, number>;
  byLocation: Record<string, number>;
  educationStats: {
    initialEducation: {
      completed: number;
      incomplete: number;
      completionRate: number;
    };
    recentEducation: {
      completed: number;
      incomplete: number;
      completionRate: number;
    };
  };
  monthlyTrends: {
    newRegistrations: Record<string, number>;
    educationCompletions: Record<string, number>;
  };
}> => {
  try {
    const querySnapshot = await getDocs(collection(db, USERS_COLLECTION));
    const users: UserRecord[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as UserRecord));

    const stats = {
      total: users.length,
      byHeadquarters: {} as Record<string, number>,
      byDepartment: {} as Record<string, number>,
      byPosition: {} as Record<string, number>,
      byAssignments: {} as Record<string, number>,
      byLocation: {} as Record<string, number>,
      educationStats: {
        initialEducation: {
          completed: 0,
          incomplete: 0,
          completionRate: 0
        },
        recentEducation: {
          completed: 0,
          incomplete: 0,
          completionRate: 0
        }
      },
      monthlyTrends: {
        newRegistrations: {} as Record<string, number>,
        educationCompletions: {} as Record<string, number>
      }
    };

    users.forEach(user => {
      // 기존 통계
      stats.byHeadquarters[user.headquarters] = (stats.byHeadquarters[user.headquarters] || 0) + 1;
      stats.byDepartment[user.department] = (stats.byDepartment[user.department] || 0) + 1;
      stats.byPosition[user.position] = (stats.byPosition[user.position] || 0) + 1;
      
      // 발령사항 통계 (배열 필드 처리)
      if (user.assignments && user.assignments.length > 0) {
        user.assignments.forEach(assignment => {
          stats.byAssignments[assignment] = (stats.byAssignments[assignment] || 0) + 1;
        });
      } else {
        stats.byAssignments['미배정'] = (stats.byAssignments['미배정'] || 0) + 1;
      }
      
      // 지점/공항 통계
      const location = user.location || '미지정';
      stats.byLocation[location] = (stats.byLocation[location] || 0) + 1;
      
      // 교육 이수 상태 통계
      const hasInitialCert = user.initialEducationCert && 
                            user.initialEducationCert.url && 
                            user.initialEducationCert.fileName;
      const hasRecentCert = user.recentEducationCert && 
                           user.recentEducationCert.url && 
                           user.recentEducationCert.fileName;
      
      if (hasInitialCert) {
        stats.educationStats.initialEducation.completed++;
      } else {
        stats.educationStats.initialEducation.incomplete++;
      }
      
      if (hasRecentCert) {
        stats.educationStats.recentEducation.completed++;
      } else {
        stats.educationStats.recentEducation.incomplete++;
      }
      
      // 월별 등록 트렌드
      if (user.createdAt) {
        const date = user.createdAt.toDate ? user.createdAt.toDate() : new Date(user.createdAt as any);
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        stats.monthlyTrends.newRegistrations[monthKey] = (stats.monthlyTrends.newRegistrations[monthKey] || 0) + 1;
      }
      
      // 월별 교육 완료 트렌드
      if (hasInitialCert && user.initialEducationCert.uploadedAt) {
        const date = user.initialEducationCert.uploadedAt.toDate ? 
                    user.initialEducationCert.uploadedAt.toDate() : 
                    new Date(user.initialEducationCert.uploadedAt as any);
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        stats.monthlyTrends.educationCompletions[monthKey] = (stats.monthlyTrends.educationCompletions[monthKey] || 0) + 1;
      }
    });

    // 교육 이수율 계산
    stats.educationStats.initialEducation.completionRate = 
      stats.total > 0 ? (stats.educationStats.initialEducation.completed / stats.total) * 100 : 0;
    stats.educationStats.recentEducation.completionRate = 
      stats.total > 0 ? (stats.educationStats.recentEducation.completed / stats.total) * 100 : 0;

    return stats;
  } catch (error: any) {
    console.error('통계 조회 실패:', error);
    throw new Error(`통계 조회 실패: ${error.message}`);
  }
};
